import React, { useEffect } from "react";
import { ToastError } from "components/Global/Toasts/Toasty";
import { withFormik } from "formik";
import _ from "lodash";
import moment from "moment";
import { v4 as uuidV4 } from "uuid";
import { PrimaryButton, ButtonSizeOptions, ButtonText, SecondaryButton, InputText, InputNumber, FormikInputMoney } from "@loanbossinc/component-library";
import { Drawer, Grid, IconButton } from "@material-ui/core";
import { MuiThemeProvider, createMuiTheme } from "@material-ui/core/styles";
import { Close, ArrowBack } from "@material-ui/icons";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";

import { SimpleInfoModal } from "../../../modals/SimpleInfoModal";

import ScheduledRents from "./scheduled-rents";
import ChargeSchedulesTypes from "./chargeScheduleTypes";
import ChargeSchedulesTypeTable from "./chargeScheduleTypeTable";
import { Header, PaddedWrapper, Title, RowContainer, StyleDateField, RentContainer, RentRow, StyledHeaderName } from "./styles";

const LeaseAddEdit = props => {
  const { selectedLease, state, setState, handleInput, setRentRollsSelections, isEditingChargeSchedule } = props;

  useEffect(() => {
    if (selectedLease) {
      const { lesseeName, leaseStartDate, leaseEndDate, monthlyCostRecovery, leaseType, scheduledRents } = selectedLease;
      // Distinguish rents and charges
      setState(prev => ({
        ...prev,
        lesseeName,
        leaseEndDate,
        leaseStartDate,
        monthlyCostRecovery,
        leaseType,
        rentsAndCharges: scheduledRents
      }));
    }
  }, [selectedLease, setState]);

  const handleDatePicker = e => {
    const { name, value } = e.target;
    if (name && value !== undefined) {
      let isEndDateError = false;
      if (moment.utc(value).isValid() && moment.utc(state.leaseEndDate).isValid() && moment.utc(state.leaseEndDate).isValid()) {
        if (name === "leaseStartDate" && moment.utc(value) > moment.utc(state.leaseEndDate)) {
          isEndDateError = true;
        }
        if (name === "leaseEndDate" && moment.utc(state.leaseStartDate) > moment.utc(value)) {
          isEndDateError = true;
        }
      }
      setState({
        ...state,
        [name]: moment.utc(value).toISOString(),
        isEndDateError,
        isAnythingWasUpdated: true
      });
    }
  };

  const theme = createMuiTheme({});

  const convertedStartDate = state.leaseStartDate ? moment.utc(state.leaseStartDate).format("YYYY-MM-DD") : "";
  const convertedEndDate = state.leaseEndDate ? moment.utc(state.leaseEndDate).format("YYYY-MM-DD") : "";

  if (isEditingChargeSchedule) {
    return <ChargeSchedulesTypeTable selectedLease={selectedLease} leaseState={state} setLeaseState={setState} />;
  }

  return (
    <>
      <RowContainer>
        <PaddedWrapper>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <StyledHeaderName style={{ paddingBottom: "0.5em" }}>LESSEE NAME</StyledHeaderName>
            <InputText onChange={handleInput} variant="filled" value={state.lesseeName} name="lesseeName" label="Name" fullWidth />
          </div>
        </PaddedWrapper>
      </RowContainer>
      <RowContainer>
        <PaddedWrapper>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <StyledHeaderName style={{ paddingBottom: "0.5em" }}>LEASE TERM</StyledHeaderName>
            <MuiThemeProvider theme={theme}>
              <Grid container alignItems="center">
                <Grid item style={{ width: "45%" }}>
                  <StyleDateField
                    value={convertedStartDate}
                    name="leaseStartDate"
                    type="date"
                    label="Start Date"
                    isEmpty={!convertedStartDate || !moment(convertedStartDate).isValid()}
                    onChange={handleDatePicker}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: "9999-12-31" }}
                  />
                </Grid>
                <Grid item container justify="center" style={{ width: "10%", color: "#5F6368", fontFamily: "Work Sans", fontSize: "12px" }}>
                  TO
                </Grid>
                <Grid item style={{ width: "45%", position: "relative" }}>
                  {state.isEndDateError && (
                    <div style={{ color: "#f44336", position: "absolute", fontSize: "12px", top: "-25px" }}>Lease end date cannot predate start date</div>
                  )}
                  <StyleDateField
                    error={state.isEndDateError}
                    value={convertedEndDate}
                    name="leaseEndDate"
                    type="date"
                    label="End Date"
                    isEmpty={!convertedEndDate || !moment(convertedEndDate).isValid()}
                    onChange={handleDatePicker}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: "9999-12-31" }}
                  />
                </Grid>
              </Grid>
            </MuiThemeProvider>
          </div>
        </PaddedWrapper>
      </RowContainer>
      <RowContainer>
        <PaddedWrapper>
          <Grid container alignItems="center">
            <Grid item style={{ width: "45%" }}>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <StyledHeaderName style={{ paddingBottom: "0.5em" }}>MONTHLY COST RECOVERY</StyledHeaderName>
                <FormikInputMoney
                  onChange={handleInput}
                  variant="filled"
                  value={state.monthlyCostRecovery}
                  name="monthlyCostRecovery"
                  label="Dollar Amount"
                  decimals={2}
                  thousandSeparator
                  fullWidth
                />
              </div>
            </Grid>
            <Grid item style={{ width: "10%" }} />

            <Grid item style={{ width: "45%" }}>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <StyledHeaderName style={{ paddingBottom: "0.5em" }}>LEASE TYPE</StyledHeaderName>
                <InputText onChange={handleInput} variant="filled" value={state.leaseType} name="leaseType" label="Lease Type" fullWidth />
              </div>
            </Grid>
          </Grid>
        </PaddedWrapper>
      </RowContainer>
      <ScheduledRents leaseState={state} setLeaseState={setState} scheduledRents={state.scheduledRents} footage={state.squareFootage} />
      <ChargeSchedulesTypes
        leaseState={state}
        setLeaseState={setState}
        chargeSchedules={state.chargeSchedules}
        footage={state.squareFootage}
        setRentRollsSelections={setRentRollsSelections}
      />
    </>
  );
};

const LeasesTable = props => {
  const { selectedSuiteCopy, onAddOrEditLease, onDeleteLease } = props;

  const getFormattedDates = (startDate, endDate) => {
    const start = moment.utc(startDate).format("M/D/YY");
    const end = moment.utc(endDate).format("M/D/YY");

    return `${start} - ${end}`;
  };

  const getLeasesRows = () => {
    if (selectedSuiteCopy.leases && selectedSuiteCopy.leases.length) {
      return selectedSuiteCopy.leases.map(lease => (
        <RentContainer key={lease.id}>
          <RentRow w="50%" align="left">
            {lease.lesseeName}
          </RentRow>
          <RentRow w="30%" align="left">
            {getFormattedDates(lease.leaseStartDate, lease.leaseEndDate)}
          </RentRow>
          <RentRow w="20%" align="right">
            <EditIcon style={{ marginLeft: "0.5em", cursor: "pointer", color: "#005AA9" }} onClick={() => onAddOrEditLease(lease.id)} />
            <DeleteIcon style={{ marginLeft: "0.5em", cursor: "pointer", color: "#005AA9" }} onClick={() => onDeleteLease(lease.id)} />
          </RentRow>
        </RentContainer>
      ));
    }

    return null;
  };

  return (
    <div style={{ marginTop: "0.5em" }}>
      <RentContainer header>
        <RentRow w="50%" align="left" bold>
          LESSEE
        </RentRow>
        <RentRow w="30%" align="left" bold>
          LEASE DATES
        </RentRow>
        <RentRow w="20%" align="right" bold>
          <ButtonText label="Add new lease" style={{ marginRight: "-0.7em" }} onClick={onAddOrEditLease} />
        </RentRow>
      </RentContainer>
      {getLeasesRows()}
    </div>
  );
};

const RentRollsDrawer = props => {
  const {
    closeDrawer,
    isOpen,
    drawerMinWidth,
    propertyId,
    dealId,
    postSuite,
    putSuite,
    deleteSuite,
    isEditingSuite,
    isEditingLease,
    isEditingChargeSchedule,
    selectedSuite,
    selectedLease,
    setRentRollsSelections,
    allSuites,
    chargeCodeAliases,
    companyId
  } = props;

  const [state, setState] = React.useState({
    suiteNumber: "",
    squareFootage: null,
    lesseeName: null,
    leaseStartDate: null,
    leaseEndDate: null,
    monthlyCostRecovery: null,
    leaseType: null,
    rentsAndCharges: [],
    scheduledRents: [],
    chargeSchedules: [],
    groupedChargeSchedules: [],
    selectedChargeScheduleGroup: null,
    editedChargeSchedules: [],
    editedChargeCodeAliases: [],
    chargeSchedulesTextInputs: { chargeCodeNickname: "", chargeCode: "" },
    isSuiteSectionValid: true,
    deleteConfirmationModal: false,
    selectedSuiteCopy: null,
    isEndDateError: false,
    dontSaveConfirmationModal: false,
    isAnythingWasUpdated: false
  });

  const defaultLeaseState = {
    lesseeName: null,
    leaseStartDate: null,
    leaseEndDate: null,
    monthlyCostRecovery: null,
    leaseType: null,
    rentsAndCharges: [],
    scheduledRents: [],
    chargeSchedules: [],
    groupedChargeSchedules: [],
    selectedChargeScheduleGroup: null,
    editedChargeSchedules: [],
    chargeSchedulesTextInputs: { chargeCodeNickname: "", chargeCode: "" },
    isEditingChargeScheduleTable: false,
    isEndDateError: false,
    isSuiteSectionValid: true
  };

  useEffect(() => {
    const rents = [];
    const charges = [];
    state.rentsAndCharges.forEach(item => {
      if (item.chargeType && item.chargeType.toLowerCase() === "rent") {
        rents.push(item);
      } else {
        charges.push(item);
      }
    });
    setState(prev => ({
      ...prev,
      scheduledRents: _.orderBy(rents, r => r.startDate, "asc"),
      chargeSchedules: _.orderBy(charges, c => c.startDate, "asc")
    }));
  }, [state.rentsAndCharges]);

  useEffect(() => {
    if (isEditingSuite && selectedSuite) {
      const selectedSuiteCopy = _.cloneDeep(selectedSuite);
      const { suiteNumber, squareFootage } = selectedSuiteCopy;
      setState({
        ...state,
        suiteNumber,
        squareFootage,
        selectedSuiteCopy,
        editedChargeCodeAliases: chargeCodeAliases
      });
    }
  }, [isEditingSuite, selectedSuite, chargeCodeAliases]);

  useEffect(() => {
    if (isOpen) {
      return;
    }

    setRentRollsSelections({
      isLoading: false,
      selectedSuite: null,
      selectedLease: null,
      isEditingSuite: false,
      isEditingLease: false,
      isEditingChargeSchedule: false
    });
    setState({
      suiteNumber: "",
      squareFootage: null,
      selectedSuiteCopy: null,
      deleteConfirmationModal: false,
      dontSaveConfirmationModal: false,
      isAnythingWasUpdated: false,
      isEditingChargeScheduleTable: false,
      ...defaultLeaseState
    });
  }, [isOpen]);

  const handleInput = e => {
    const { name, value } = e.target;
    let formattedValue;
    if (name === "squareFootage") {
      formattedValue = parseInt(value?.replace(/,/g, ""), 10);
    } else if (name === "monthlyCostRecovery") {
      formattedValue = parseFloat(value);
    } else {
      formattedValue = value;
    }
    setState({
      ...state,
      isAnythingWasUpdated: true,
      [name]: formattedValue
    });
  };

  const areLeaseTermsOverlapping = leases => {
    if (leases && leases.length > 0) {
      return leases.some(lease => {
        const aStart = moment.utc(lease.leaseStartDate);
        const aEnd = moment.utc(lease.leaseEndDate);
        const bStart = moment.utc(state.leaseStartDate);
        const bEnd = moment.utc(state.leaseEndDate);
        if (selectedLease && selectedLease.id === lease.id) {
          return false;
        }
        // b starts in a
        if (aStart <= bStart && bStart <= aEnd) {
          return true;
        }
        // b ends in a
        if (aStart <= bEnd && bEnd <= aEnd) {
          return true;
        }
        // a in b
        if (bStart < aStart && aEnd < bEnd) {
          return true;
        }

        return false;
      });
    }

    return false;
  };

  const onSaveChargeSchedules = () => {
    const chargeCode = state.chargeSchedulesTextInputs.chargeCode;
    const chargeCodeNickname = state.chargeSchedulesTextInputs.chargeCodeNickname;

    // validate chargeCode and nickname
    const selectedGroupChargeCode = state.selectedChargeScheduleGroup?.chargeCode;
    const existingChargeCode = state.rentsAndCharges.find(charge => {
      if (state.selectedChargeScheduleGroup && charge.chargeCode === selectedGroupChargeCode) {
        return false;
      }

      return charge.chargeCode === chargeCode;
    });

    if (existingChargeCode) {
      ToastError("Charge code already exists");

      return;
    }

    let updatedAliases = state.editedChargeCodeAliases;
    if (!state.selectedChargeScheduleGroup) {
      const existingNickname = state.editedChargeCodeAliases.find(alias => alias.alias === chargeCodeNickname);
      if (existingNickname) {
        ToastError("Charge code nickname already exists");

        return;
      }
      if (chargeCodeNickname !== "") {
        updatedAliases = [...state.editedChargeCodeAliases, { chargeCode, alias: chargeCodeNickname }];
      }
    } else {
      const filteredAliases = state.editedChargeCodeAliases?.filter(alias => alias.chargeCode !== selectedGroupChargeCode);
      const existingNickname = filteredAliases?.find(alias => alias.alias === chargeCodeNickname);
      if (existingNickname) {
        ToastError("Charge code nickname already exists");

        return;
      }
      if (chargeCodeNickname !== "") {
        updatedAliases = [...filteredAliases, { chargeCode, alias: chargeCodeNickname }];
      } else {
        updatedAliases = filteredAliases;
      }
    }

    if (!state.selectedChargeScheduleGroup) {
      setState(prev => ({
        ...prev,
        rentsAndCharges: [...prev.rentsAndCharges, ...prev.editedChargeSchedules],
        editedChargeCodeAliases: updatedAliases
      }));
    } else {
      const updatedChargeSchedules = [];
      state.groupedChargeSchedules.forEach(group => {
        if (group.chargeCode !== state.selectedChargeScheduleGroup.chargeCode) {
          updatedChargeSchedules.push(...group.charges);
        } else {
          updatedChargeSchedules.push(...state.editedChargeSchedules);
        }
      });

      setState(prev => ({
        ...prev,
        rentsAndCharges: [...prev.scheduledRents, ...updatedChargeSchedules],
        editedChargeCodeAliases: updatedAliases
      }));
    }
    setRentRollsSelections({ isEditingChargeSchedule: false });
  };

  const onSaveLease = () => {
    const { lesseeName, leaseStartDate, leaseEndDate, leaseType, monthlyCostRecovery, selectedSuiteCopy } = state;
    if (areLeaseTermsOverlapping(selectedSuiteCopy.leases)) {
      ToastError("Lease terms overlap existing lease");

      return;
    }
    let updatedLeases;
    let newLease;
    if (selectedLease) {
      updatedLeases = state.selectedSuiteCopy?.leases?.map(lease => {
        if (lease.id !== selectedLease.id) {
          return lease;
        }

        return {
          ...selectedLease,
          lesseeName,
          leaseStartDate,
          leaseEndDate,
          leaseType,
          monthlyCostRecovery,
          scheduledRents: state.rentsAndCharges
        };
      });
    } else {
      newLease = {
        id: uuidV4(),
        lesseeName,
        leaseStartDate,
        leaseEndDate,
        leaseType,
        monthlyCostRecovery,
        scheduledRents: state.rentsAndCharges
      };
    }

    setState(prev => ({
      ...prev,
      ...defaultLeaseState,
      selectedSuiteCopy: selectedLease ?
        { ...prev.selectedSuiteCopy, leases: updatedLeases } :
        { ...prev.selectedSuiteCopy, leases: [...prev.selectedSuiteCopy.leases, newLease] }
    }));

    setRentRollsSelections({ isEditingLease: false });
  };

  const onSaveSuite = () => {
    if (!isEditingLease && !isEditingSuite) {
      // * SUITE SAVE
      const suiteExists =
        allSuites && Boolean(allSuites.find(suite => suite.suiteNumber === state.suiteNumber && suite.squareFootage === Number(state.squareFootage)));
      if (suiteExists) {
        ToastError("Suite already exists");
      } else {
        postSuite({
          data: {
            dealId,
            companyId,
            propertyId,
            suiteNumber: state.suiteNumber,
            leases: [],
            squareFootage: state.squareFootage
          }
        });
      }
    } else {
      const suiteExists =
        allSuites &&
        Boolean(allSuites.find(
          suite => suite.id !== state.selectedSuiteCopy.id && suite.suiteNumber === state.suiteNumber && suite.squareFootage === Number(state.squareFootage)
        ));
      if (suiteExists) {
        ToastError("Suite already exists");
      } else {
        putSuite({
          data: {
            ...state.selectedSuiteCopy,
            suiteNumber: state.suiteNumber,
            squareFootage: state.squareFootage
          }
        });
        closeDrawer();
      }
    }
  };

  const findOutLocation = () => {
    if (isEditingChargeSchedule) {
      return "chargeSchedules";
    }
    if (isEditingLease) {
      return "lease";
    }

    return "suite";
  };

  const onSaveClick = location => {
    switch (location) {
      case "chargeSchedules":
        onSaveChargeSchedules();
        break;
      case "lease":
        onSaveLease();
        break;
      case "suite":
        onSaveSuite();
        break;
      default:
        break;
    }
  };

  const onAddOrEditLease = id => {
    const leaseToEdit = state.selectedSuiteCopy?.leases?.find(lease => lease.id === id);
    setRentRollsSelections({
      isEditingLease: true,
      selectedLease: leaseToEdit || null
    });
  };

  const onDeleteLease = id => {
    const filteredLeases = state.selectedSuiteCopy.leases?.filter(lease => lease.id !== id);
    setState(prev => ({
      ...prev,
      selectedSuiteCopy: { ...prev.selectedSuiteCopy, leases: filteredLeases },
      isAnythingWasUpdated: true
    }));
  };

  const openConfirmationModal = () => setState({ ...state, deleteConfirmationModal: true });

  const onBackToSuiteOrLease = () => {
    if (isEditingChargeSchedule) {
      setRentRollsSelections({
        isEditingChargeSchedule: false,
        selectedChargeSchedule: null
      });
    } else {
      setRentRollsSelections({
        isEditingLease: false,
        selectedLease: null
      });
      setState(prev => ({
        ...prev,
        ...defaultLeaseState
      }));
    }
  };

  const getDrawerTitle = () => {
    if (isEditingChargeSchedule) {
      return state.selectedChargeScheduleGroup ? "Edit Charge Type" : "Add New Charge Type";
    }
    if (selectedSuite) {
      const { suiteNumber } = selectedSuite;
      if (isEditingLease) {
        return `${suiteNumber} - ${selectedLease ? "Edit lease" : "Add new lease"}`;
      }

      return suiteNumber;
    }

    return "Add new suite";
  };

  const getHeaderSaveButtonLabel = () => {
    if (isEditingChargeSchedule) {
      return "Save Type";
    }
    if (isEditingLease) {
      return "Save Lease";
    }

    return "Save Suite";
  };

  const showDeleteSuiteButton = selectedSuite && !isEditingLease && !isEditingChargeSchedule;

  const isAbleToSave = () => {
    if (!isEditingLease) {
      return Boolean(state.suiteNumber) && state.squareFootage !== null;
    }
    const isLeaseValid = Boolean(state.lesseeName) && Boolean(state.leaseStartDate) && Boolean(state.leaseEndDate) && !state.isEndDateError;
    if (isEditingChargeSchedule) {
      const rowsAndChargeCodeAdded = Boolean(state.editedChargeSchedules.length) && Boolean(state.editedChargeSchedules[0].chargeCode);

      return !state.isEditingChargeScheduleTable && isLeaseValid && rowsAndChargeCodeAdded;
    }

    return state.isSuiteSectionValid && isLeaseValid;
  };

  const onDeleteSuiteSuccess = () => {
    deleteSuite({ id: state.selectedSuiteCopy.id });
    setState(prev => ({
      ...prev,
      deleteConfirmationModal: false
    }));
    closeDrawer();
  };

  const handleCloseDrawer = () => {
    if (!state.isAnythingWasUpdated) {
      closeDrawer();
    } else {
      setState(prev => ({ ...prev, dontSaveConfirmationModal: true }));
    }
  };

  return (
    <Drawer
      onClose={handleCloseDrawer}
      anchor="right"
      open={isOpen}
      PaperProps={{ style: { minWidth: drawerMinWidth, width: drawerMinWidth } }}
      ModalProps={{
        onBackdropClick: handleCloseDrawer
      }}
    >
      <Header>
        <Grid container justify="flex-start" alignItems="center" direction="row" style={{ paddingLeft: "0.2em" }}>
          {isEditingLease || isEditingChargeSchedule ? (
            <IconButton onClick={onBackToSuiteOrLease}>
              <ArrowBack />
            </IconButton>
          ) : (
            <IconButton onClick={handleCloseDrawer}>
              <Close style={{ color: "#C40A0A" }} />
            </IconButton>
          )}
          <div style={{ flexGrow: 1, paddingLeft: "0.2em" }}>
            <Title>{getDrawerTitle()}</Title>
          </div>
          <PaddedWrapper>
            {showDeleteSuiteButton && (
              <SecondaryButton
                style={{ marginRight: ".5em", color: "#C40A0A", border: "1px #C40A0A solid", fontSize: "14px" }}
                label="Delete Suite"
                onClick={openConfirmationModal}
                size={ButtonSizeOptions.sm}
              />
            )}
            <PrimaryButton
              style={{ fontSize: "14px" }}
              label={getHeaderSaveButtonLabel()}
              alignRight
              disabled={!isAbleToSave()}
              onClick={() => onSaveClick(findOutLocation())}
              size={ButtonSizeOptions.sm}
            />
          </PaddedWrapper>
        </Grid>
      </Header>
      {!isEditingLease ? (
        // * SUITE DRAWER STATE
        <>
          <RowContainer>
            <Grid container justify="flex-start" alignItems="center" direction="row">
              <Grid item xs={6}>
                <PaddedWrapper>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <StyledHeaderName style={{ paddingBottom: "0.5em" }}>SUITE NUMBER</StyledHeaderName>
                    <InputText onChange={handleInput} variant="filled" value={state.suiteNumber} name="suiteNumber" label="Number" fullWidth />
                  </div>
                </PaddedWrapper>
              </Grid>
              <Grid item xs={6}>
                <PaddedWrapper>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <StyledHeaderName style={{ paddingBottom: "0.5em" }}>SQUARE FEET</StyledHeaderName>
                  </div>
                  <InputNumber
                    onChange={handleInput}
                    variant="filled"
                    value={state.squareFootage}
                    name="squareFootage"
                    fullWidth
                    thousandSeparator
                    label="Number"
                  />
                </PaddedWrapper>
              </Grid>
            </Grid>
          </RowContainer>
          {isEditingSuite && (
            <RowContainer>
              <PaddedWrapper>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <StyledHeaderName>LEASES</StyledHeaderName>
                    {!state.selectedSuiteCopy?.leases?.length && (
                      <ButtonText label="Add new lease" style={{ marginRight: "-0.7em" }} onClick={onAddOrEditLease} />
                    )}
                  </div>
                  {Boolean(state.selectedSuiteCopy?.leases?.length) && (
                    <LeasesTable selectedSuiteCopy={state.selectedSuiteCopy} onAddOrEditLease={onAddOrEditLease} onDeleteLease={onDeleteLease} />
                  )}
                </div>
              </PaddedWrapper>
            </RowContainer>
          )}
        </>
      ) : (
        // * LEASE DRAWER STATE
        <LeaseAddEdit
          selectedLease={selectedLease}
          setRentRollsSelections={setRentRollsSelections}
          state={state}
          setState={setState}
          handleInput={handleInput}
          isEditingChargeSchedule={isEditingChargeSchedule}
        />
      )}

      {state.deleteConfirmationModal && (
        <SimpleInfoModal
          title="Delete suite"
          isOpen={state.deleteConfirmationModal}
          onAccept={onDeleteSuiteSuccess}
          content="Deleting this suite will delete all its underlying data and can not be undone. Are you sure that you want to do this?"
          onClose={() => setState({ ...state, deleteConfirmationModal: false })}
        />
      )}
      {state.dontSaveConfirmationModal && (
        <SimpleInfoModal
          title="Changes are not saved"
          isOpen={state.dontSaveConfirmationModal}
          onAccept={closeDrawer}
          content="Your changes have not been saved. Are you sure you want to discard the changes?"
          onClose={() => setState(prev => ({ ...prev, dontSaveConfirmationModal: false }))}
        />
      )}
    </Drawer>
  );
};

// withFormik used only in order to use InputMoney component from LB CL
export default withFormik({})(RentRollsDrawer);
